import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, AlertTriangle, Send, RefreshCw, Stethoscope } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

function Row({ ok, label, detail, testid }) {
  const Icon = ok === true ? CheckCircle2 : ok === false ? XCircle : AlertTriangle;
  const color = ok === true ? "text-emerald-600" : ok === false ? "text-red-600" : "text-amber-600";
  return (
    <div className="flex items-start gap-2 text-sm" data-testid={testid}>
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${color}`} />
      <div><p className="text-slate-800">{label}</p>{detail && <p className="text-xs text-slate-500 break-all">{detail}</p>}</div>
    </div>
  );
}

export function DeliveryHealthButton({ onClick }) {
  return <Button variant="outline" className="h-9 gap-1.5" onClick={onClick} data-testid="ld-health-open"><Stethoscope className="h-4 w-4" /> Delivery check</Button>;
}

export function DeliveryHealth({ onClose }) {
  const [test, setTest] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data, isFetching, refetch } = useQuery({ queryKey: ["ld-health"], queryFn: async () => (await api.get("/lead-desk/delivery-health")).data });

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/lead-desk/test-email");
      setTest({ ...r, events: [] });
      toast.success(`Brevo accepted the test email to ${r.to}`);
      setTimeout(async () => {
        try { const { data: s } = await api.get(`/lead-desk/test-email/${encodeURIComponent(r.message_id)}`); setTest((t) => ({ ...t, events: s.events })); } catch { /* noop */ }
      }, 8000);
    } catch (e) { toast.error(apiError(e)); setTest({ error: apiError(e) }); } finally { setBusy(false); }
  };

  const refreshTest = async () => {
    if (!test?.message_id) return;
    try { const { data: s } = await api.get(`/lead-desk/test-email/${encodeURIComponent(test.message_id)}`); setTest((t) => ({ ...t, events: s.events })); } catch (e) { toast.error(apiError(e)); }
  };

  const c = data?.connection;
  return (
    <Card className="border-amber-200 bg-amber-50/40 p-4 space-y-3" data-testid="ld-health-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-900">Why aren't catalogue emails arriving?</p>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" className="h-8 gap-1" onClick={() => refetch()} disabled={isFetching} data-testid="ld-health-refresh"><RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} /> Re-check</Button>
          <Button size="sm" variant="ghost" className="h-8" onClick={onClose} data-testid="ld-health-close">Close</Button>
        </div>
      </div>
      {!data ? <div className="py-4 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Row testid="ld-health-conn" ok={c?.ok} label={c?.ok ? `Brevo connection OK (account ${c.account_email})` : "Brevo is refusing this server"} detail={c?.ok ? `API key from ${data.key_source}${c.plans?.length ? " · plan: " + c.plans.map((p) => `${p.type}${p.credits != null ? ` ${p.credits} ${p.credits_type || ""} credits` : ""}`).join(", ") : ""}` : c?.hint || c?.error} />
            {c?.ok && c.plans?.some((p) => p.credits === 0) && <Row testid="ld-health-credits" ok={false} label="Brevo reports 0 credits on a plan — sends are accepted but held until the plan is topped up / invoice paid" />}
            {c?.ok && <Row testid="ld-health-sender" ok={data.sender_verified} label={`Sender ${data.sender?.email || "(not set)"} ${data.sender_verified ? "is verified in Brevo" : data.sender_verified === false ? "is NOT a verified sender — Brevo drops or spam-folders these" : "could not be checked"}`} detail={data.sender_verified === false ? data.sender_reason : data.sender_error} />}
            {c?.ok && <Row testid="ld-health-webhook" ok={data.webhook?.configured} label={data.webhook?.configured ? "Transactional webhook → CRM is set up" : data.webhook?.configured === false ? "No transactional webhook to the CRM — statuses won't update by themselves" : "Webhook could not be checked"} detail={data.webhook?.configured ? data.webhook.events.join(", ") : data.webhook?.error || "Brevo → Transactional → Settings → Webhooks → add this CRM's /api/marketing/webhooks/brevo URL"} />}
            {c?.ok && (
              <div className="pt-2 space-y-2">
                <Button size="sm" className="h-8 gap-1.5" onClick={sendTest} disabled={busy} data-testid="ld-health-test-send">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send a test email to me</Button>
                {test?.error && <p className="text-xs text-red-600" data-testid="ld-health-test-error">{test.error}</p>}
                {test?.message_id && (
                  <div className="text-xs text-slate-600 space-y-1" data-testid="ld-health-test-result">
                    <p>Accepted by Brevo for <b>{test.to}</b> from {test.sender}. Message id <span className="font-mono">{test.message_id}</span></p>
                    <p className="flex items-center gap-2">Brevo events: {test.events?.length ? test.events.map((e) => `${e.event}${e.reason ? ` (${e.reason})` : ""}`).join(" → ") : "none yet"} <button className="text-sky-700 hover:underline" onClick={refreshTest} data-testid="ld-health-test-refresh">refresh</button></p>
                    <p className="text-slate-400">Now check your inbox, Spam and Promotions for "Lead Desk test email".</p>
                  </div>
                )}
              </div>
            )}
          </div>
          {c?.ok && (
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Last Lead Desk emails as Brevo sees them (30 days)</p>
              {data.events_error && <p className="text-xs text-red-600">{data.events_error}</p>}
              {!data.recent_events?.length ? <p className="text-xs text-slate-500" data-testid="ld-health-no-events">Brevo has no record of any Lead Desk email in the last 30 days — the sends never reached Brevo.</p> : (
                <ul className="text-xs divide-y divide-slate-200 border border-slate-200 rounded bg-white max-h-56 overflow-y-auto" data-testid="ld-health-events">
                  {data.recent_events.map((e, i) => (
                    <li key={i} className="px-2 py-1.5 flex justify-between gap-2"><span className="truncate">{e.email} <span className={["hard_bounce", "soft_bounce", "blocked", "spam", "invalid_email", "error"].includes(e.event) ? "text-red-600" : "text-emerald-700"}>· {e.event}</span>{e.reason ? <span className="text-slate-400"> — {e.reason}</span> : null}</span><span className="text-slate-400 whitespace-nowrap">{e.at ? new Date(e.at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</span></li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
